/** Hardcoded dropdown options for the Contactos module. Mirrors the PowerApps source. */

export const MEDIOS_DE_LLEGADA = [
  'Instagram',
  'Meta',
  'TikTok',
  'X',
  'Referidos',
  'Otro',
] as const;
export type MedioDeLlegada = (typeof MEDIOS_DE_LLEGADA)[number];

/** Provincias argentinas — used by the Datos fiscales dropdown. */
export const PROVINCIAS_ARGENTINA = [
  'Buenos Aires',
  'CABA',
  'Catamarca',
  'Chaco',
  'Chubut',
  'Córdoba',
  'Corrientes',
  'Entre Ríos',
  'Formosa',
  'Jujuy',
  'La Pampa',
  'La Rioja',
  'Mendoza',
  'Misiones',
  'Neuquén',
  'Río Negro',
  'Salta',
  'San Juan',
  'San Luis',
  'Santa Cruz',
  'Santa Fe',
  'Santiago del Estero',
  'Tierra del Fuego',
  'Tucumán',
] as const;
export type ProvinciaArgentina = (typeof PROVINCIAS_ARGENTINA)[number];

/**
 * Entidades federativas de México — the 32 official states. Used by the
 * Datos fiscales dropdown when the active country is MX, parallel to
 * `PROVINCIAS_ARGENTINA` for AR. Forms surface this as "Estado" rather
 * than "Provincia" on MX.
 */
export const ESTADOS_MX = [
  'Aguascalientes',
  'Baja California',
  'Baja California Sur',
  'Campeche',
  'Chiapas',
  'Chihuahua',
  'Ciudad de México',
  'Coahuila',
  'Colima',
  'Durango',
  'Estado de México',
  'Guanajuato',
  'Guerrero',
  'Hidalgo',
  'Jalisco',
  'Michoacán',
  'Morelos',
  'Nayarit',
  'Nuevo León',
  'Oaxaca',
  'Puebla',
  'Querétaro',
  'Quintana Roo',
  'San Luis Potosí',
  'Sinaloa',
  'Sonora',
  'Tabasco',
  'Tamaulipas',
  'Tlaxcala',
  'Veracruz',
  'Yucatán',
  'Zacatecas',
] as const;
export type EstadoMx = (typeof ESTADOS_MX)[number];

/**
 * Mexican RFC (Registro Federal de Contribuyentes) format. Accepts both
 * persona-física (13 chars) and persona-moral (12 chars) shapes —
 * letters + digits, optional dash anywhere (some users paste it with
 * the homoclave separated). Length is the load-bearing check; SAT
 * structural rules are out of scope here.
 */
export const RFC_REGEX = /^[A-ZÑ&]{3,4}-?\d{6}-?[A-Z0-9]{3}$/i;

/**
 * Condiciones IVA Argentina. PowerApps hardcoded just 'Responsable Inscripto';
 * we include the full set so Compras / Facturación can reuse the same enum.
 */
export const CONDICIONES_IVA = [
  'Responsable Inscripto',
  'Monotributista',
  'Exento',
  'Consumidor Final',
  'No Responsable',
  'Sujeto No Categorizado',
] as const;
export type CondicionIVA = (typeof CONDICIONES_IVA)[number];

/**
 * Condición IVA de un cliente de exportación (Factura E). Va aparte de CONDICIONES_IVA porque esa
 * lista también la usan los Proveedores. Con este valor el contacto pide país / tipo de entidad /
 * ID impositivo en vez de CUIT. El string tiene que coincidir con el que lee el server
 * (CONDICION_CLIENTE_EXTERIOR en server/src/lib/arca/exportacion.ts).
 */
export const CONDICION_CLIENTE_EXTERIOR = 'Cliente del Exterior';
export const CONDICIONES_IVA_CONTACTO = [...CONDICIONES_IVA, CONDICION_CLIENTE_EXTERIOR] as const;

/**
 * SAT c_UsoCFDI catalog — the "Uso del CFDI" the receptor declares to
 * SAT when an invoice is issued in MX. Nova needs this value to stamp
 * the CFDI 4.0 factura, so we collect it on the contacto and surface it
 * on the Nova confirm dialogs.
 *
 * Subset of the full catalog: only the codes that apply to non-public
 * receptors and that B2B/B2C invoicing actually uses. P01 ("Por
 * definir") is the SAT fallback when the receptor hasn't told the
 * emisor yet.
 */
export const USOS_CFDI = [
  { code: 'G01', label: 'Adquisición de mercancías' },
  { code: 'G02', label: 'Devoluciones, descuentos o bonificaciones' },
  { code: 'G03', label: 'Gastos en general' },
  { code: 'I01', label: 'Construcciones' },
  { code: 'I02', label: 'Mobiliario y equipo de oficina por inversiones' },
  { code: 'I03', label: 'Equipo de transporte' },
  { code: 'I04', label: 'Equipo de cómputo y accesorios' },
  { code: 'I05', label: 'Dados, troqueles, moldes, matrices y herramental' },
  { code: 'I06', label: 'Comunicaciones telefónicas' },
  { code: 'I07', label: 'Comunicaciones satelitales' },
  { code: 'I08', label: 'Otra maquinaria y equipo' },
  { code: 'D01', label: 'Honorarios médicos, dentales y gastos hospitalarios' },
  { code: 'D02', label: 'Gastos médicos por incapacidad o discapacidad' },
  { code: 'D03', label: 'Gastos funerales' },
  { code: 'D04', label: 'Donativos' },
  { code: 'D05', label: 'Intereses reales efectivamente pagados por créditos hipotecarios' },
  { code: 'D06', label: 'Aportaciones voluntarias al SAR' },
  { code: 'D07', label: 'Primas por seguros de gastos médicos' },
  { code: 'D08', label: 'Gastos de transportación escolar obligatoria' },
  { code: 'D09', label: 'Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones' },
  { code: 'D10', label: 'Pagos por servicios educativos (colegiaturas)' },
  { code: 'S01', label: 'Sin efectos fiscales' },
  { code: 'CP01', label: 'Pagos' },
  { code: 'CN01', label: 'Nómina' },
  { code: 'P01', label: 'Por definir' },
] as const;
export type UsoCFDICode = (typeof USOS_CFDI)[number]['code'];
